/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';

const placeholder = '-';

function getCurrencyTextWithoutCurrencyCode(currencyCode, amount, i18n, maxFractionDigits) {
  const currencySymbol = i18n.getCurrencySymbol(currencyCode);
  return _.isFinite(amount) && currencySymbol ?
    i18n.formatCurrency(
      amount,
      currencyCode,
      { maximumFractionDigits: Math.max(maxFractionDigits, 0) }
    ) :
    `${currencySymbol || ''} ${_.isString(amount) ? amount : placeholder}`;
}

/**
 * Gets Currency as text
 * @property {String} currencyCode - the currency code: https://www.iso.org/iso-4217-currency-codes.html
 * @property {Number | String} amount - the money amount, when amount is string will return amount string with currency
 * @property {Object} i18n - i18n object which offers localized formatter
 * @property {Number} maxFractionDigits - non-negative integer for the maximum decimal digits
 * @property {Boolean} hideCurrencyCode - Whether to show the currency code after the value
 * @property {String} separator - The separator to join with between amount and currency code
*/

export function getCurrencyText({
  currencyCode,
  amount,
  i18n,
  maxFractionDigits = 2,
  hideCurrencyCode = false,
  separator = '',
}) {
  const currencyTextWithoutCurrencyCode = getCurrencyTextWithoutCurrencyCode(
    currencyCode,
    amount,
    i18n,
    maxFractionDigits
  );
  return hideCurrencyCode || !_.isString(currencyCode) ?
    currencyTextWithoutCurrencyCode :
    `${currencyTextWithoutCurrencyCode}${separator}${currencyCode}`;
}

/**
 * Currency Props
 * @property {String} name - name of the currency component
 * @property {Array} containerClass - list of class names to decorate the container
 * @property {Object} i18n - i18n object which offers localized formatter
 * @property {String} currencyCode - the currency code: https://www.iso.org/iso-4217-currency-codes.html
 * @property {Array} currencyClass - list of class names to decorate the currency part
 * @property {Number} amount - the money amount
 * @property {Array} amountClass - list of class names to decorate the amount part
 * @property {Number} maxFractionDigits - non-negative integer for the maximum decimal digits
 * @property {Boolean} hideCurrencyCode - Whether to show the currency code after the value
*/

export function Currency({
  name,
  containerClass,
  i18n,
  currencyCode,
  currencyClass,
  amount,
  amountClass,
  maxFractionDigits,
  hideCurrencyCode,
}) {
  const formatterClassName = `currency-${name || _.uniqueId()}`;
  const amountWithCurrency = getCurrencyTextWithoutCurrencyCode(
    currencyCode,
    amount,
    i18n,
    maxFractionDigits
  );

  return (
    <div className={classNames(formatterClassName, containerClass)}>
      <span className={classNames('currency-amount', amountClass)}>{amountWithCurrency}</span>
      {!hideCurrencyCode && <span className={classNames('currency-code', currencyClass)}>{currencyCode}</span>}
    </div>
  );
}

Currency.propTypes = {
  name: PropTypes.string,
  containerClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  i18n: PropTypes.shape({
    formatCurrency: PropTypes.func.isRequired,
    getCurrencySymbol: PropTypes.func.isRequired,
  }).isRequired,
  currencyCode: PropTypes.string,
  currencyClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  amount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  amountClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  maxFractionDigits: PropTypes.number,
  hideCurrencyCode: PropTypes.bool,
};

Currency.defaultProps = {
  name: '',
  containerClass: [],
  amount: undefined,
  currencyCode: undefined,
  currencyClass: [],
  amountClass: [],
  maxFractionDigits: 2,
  hideCurrencyCode: false,
};
